import type { ChipTone } from '../shared/listPage';

/**
 * Κοινά των καρτελών των κεντρικών: ονομασία καταστήματος, ώρες Ελλάδας σε 24ωρη μορφή, ετικέτες
 * κατάστασης/τύπου/πληρωμής. Τα κείμενα είναι κλειδιά του namespace «orgDashboard».
 */

/** Το t του i18next ως απλή συνάρτηση (όπως στην Αρχική): `useTranslation().t as unknown as TFn`. */
export type TFn = (key: string, opts?: Record<string, unknown>) => string;

/** Η φιλική ονομασία του υποκαταστήματος, αλλιώς η επωνυμία (όπως πάντα). */
export const storeLabel = (s: { display_name?: string | null; name?: string | null } | null | undefined): string =>
  (s?.display_name && String(s.display_name).trim() ? String(s.display_name) : (s?.name || ''));

// Ώρα Ελλάδας και 24ωρη, όπως η Αρχική και οι Κλήσεις (ως 14/10 ο browser έγραφε «10:15 μ.μ.» στη δική του ζώνη).
const ATHENS = 'Europe/Athens';

/** «14/10, 22:15» — ή με έτος «14/10/2026, 22:15». */
export const fmtDateTime = (iso: string | null | undefined, withYear = false): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('el-GR', {
    timeZone: ATHENS, day: '2-digit', month: '2-digit', ...(withYear ? { year: 'numeric' } : {}),
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  });
};

/** «14/10/2026» */
export const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('el-GR', { timeZone: ATHENS, day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** Κατάσταση παραγγελίας ή παραστατικού → ετικέτα + χρώμα (ίδιες τιμές με τον παλιό statusBadge). */
const STATUS_TONE: Record<string, ChipTone> = {
  completed: 'ok', approved: 'ok', success: 'ok', delivered: 'ok', dispatched: 'ok',
  pending: 'warn', accepted: 'muted', preparing: 'muted', ready: 'muted',
  cancelled: 'danger', rejected: 'danger', failed: 'danger',
};

export const statusChip = (t: TFn, status: string | null | undefined): { tone: ChipTone; label: string } => {
  const key = String(status || '');
  if (key && STATUS_TONE[key]) return { tone: STATUS_TONE[key], label: t(`orgDashboard:status.${key}`) };
  return { tone: 'muted', label: key || '—' };
};

/** Τύπος παραγγελίας (order_type / κανάλι των αναλυτικών). */
export const orderTypeLabel = (t: TFn, type: string | null | undefined): string => {
  switch (type) {
    case 'takeaway':
    case 'take_away': return t('orgDashboard:channel.takeaway');
    case 'delivery': return t('orgDashboard:channel.delivery');
    case 'dine_in':
    case 'dinein': return t('orgDashboard:channel.dineIn');
    case 'preorder': return t('orgDashboard:channel.preorder');
    case 'unknown': return t('orgDashboard:channel.other');
    default: return type || '—';
  }
};

/** Τρόπος πληρωμής της παραγγελίας (payment_type). Άγνωστος → όπως ήρθε. */
export const paymentLabel = (t: TFn, method: string | null | undefined): string => {
  const known = ['prepaid', 'cash', 'card', 'pos', 'iris', 'credit', 'online'];
  if (method && known.includes(method)) return t(`orgDashboard:payment.${method}`);
  if (!method || method === 'unknown') return '—';
  return method;
};

/** Κανάλι/πλατφόρμα της παραγγελίας (platform_name). */
export const platformLabel = (t: TFn, platform: string | null | undefined): string => {
  switch (platform) {
    case 'internal': return t('orgDashboard:platform.internal');
    case 'efood': return 'efood';
    case 'wolt': return 'Wolt';
    case 'box': return 'BOX';
    default: return platform || '—';
  }
};

/** Ρόλος μέλους στον οργανισμό. */
export const roleLabel = (t: TFn, role: string | null | undefined): string => {
  switch (role) {
    case 'org_owner': return t('admin:roleOwner2');
    case 'org_admin': return t('admin:roleAdmin2');
    case 'org_viewer': return t('admin:roleViewer2');
    case 'org_editor': return t('orgDashboard:roleEditor');
    default: return role || '—';
  }
};

/**
 * Τύπος παραστατικού ΑΑΔΕ → όνομα. Οι κωδικοί (11.1, 1.1…) δεν λένε τίποτα στον χρήστη. Τα ονόματα ζουν
 * στο i18n (orgDashboard:docType.11_1 — το «.» είναι διαχωριστικό κλειδιών)· αυτός ο πίνακας μένει ως
 * εφεδρικό και για όποιον τον εισάγει από το OrgRecordDetailsModal.
 */
export const DOC_TYPE_LABELS: Record<string, string> = {
  '11.1': 'Απόδειξη λιανικής',
  '11.2': 'Απόδειξη παροχής υπηρεσιών',
  '11.3': 'Απλοποιημένο τιμολόγιο',
  '11.4': 'Πιστωτικό λιανικής',
  '11.5': 'Απόδειξη λιανικής (ειδική)',
  '1.1': 'Τιμολόγιο πώλησης',
  '1.2': 'Τιμολόγιο πώλησης (ενδοκοινοτικό)',
  '1.6': 'Τιμολόγιο πώλησης — λοιπά',
  '2.1': 'Τιμολόγιο παροχής υπηρεσιών',
  '2.4': 'Τιμολόγιο παροχής — λοιπά',
  '5.1': 'Πιστωτικό τιμολόγιο',
  '5.2': 'Πιστωτικό τιμολόγιο',
  '8.6': 'Δελτίο παραγγελίας',
  '9.3': 'Δελτίο αποστολής',
};

export const docTypeLabel = (t: TFn, code: string | null | undefined): string => {
  if (!code) return '—';
  if (!DOC_TYPE_LABELS[code]) return code;
  return t(`orgDashboard:docType.${code.replace(/\./g, '_')}`, { defaultValue: DOC_TYPE_LABELS[code] });
};

/** Ποσοστό μεταβολής όπως πάντα: χωρίς προηγούμενο, 100% αν υπάρχει τωρινό. */
export const pctChange = (cur: number, prev: number): number =>
  (prev > 0 ? Math.round(((cur - prev) / prev) * 100) : (cur > 0 ? 100 : 0));

